import React, { useState, useEffect } from 'react';
import {
  Plus, Play, Edit, Trash2, Clock, CheckCircle, XCircle, AlertCircle,
  RefreshCw, Power, Settings, Zap
} from 'lucide-react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { useTenantProtection } from '../../../hooks/useTenantProtection';
import JobForm from './JobForm';

const JobList = ({ onStatsChange }) => {
  const { getProtectedTenantCode } = useTenantProtection();

  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showJobForm, setShowJobForm] = useState(false);
  const [editingJob, setEditingJob] = useState(null);
  const [executingJobId, setExecutingJobId] = useState(null);

  useEffect(() => {
    loadJobs();
  }, []);

  const loadJobs = async () => {
    try {
      setLoading(true);
      const tenantCode = getProtectedTenantCode();
      const response = await axios.get('/api/automation/jobs', {
        headers: { 'X-Tenant-Code': tenantCode },
      });
      setJobs(response.data);
    } catch (error) {
      console.error('Failed to load jobs:', error);
      toast.error('Failed to load jobs');
    } finally {
      setLoading(false);
    }
  };

  const handleExecuteJob = async (job) => {
    if (job.ExecutionMode === 'scheduled' && !window.confirm('This is a scheduled job. Are you sure you want to execute it manually?')) {
      return;
    }

    try {
      setExecutingJobId(job.JobID);
      const tenantCode = getProtectedTenantCode();
      const response = await axios.post(
        `/api/automation/jobs/${job.JobID}/execute`,
        {},
        { headers: { 'X-Tenant-Code': tenantCode } }
      );
      toast.success('Job queued for execution');
      loadJobs();
    } catch (error) {
      console.error('Failed to execute job:', error);
      toast.error(error.response?.data?.error || 'Failed to execute job');
    } finally {
      setExecutingJobId(null);
    }
  };

  const handleToggleJob = async (job) => {
    try {
      const tenantCode = getProtectedTenantCode();
      await axios.put(
        `/api/automation/jobs/${job.JobID}`,
        { isEnabled: !job.IsEnabled },
        { headers: { 'X-Tenant-Code': tenantCode } }
      );
      toast.success(`Job ${!job.IsEnabled ? 'enabled' : 'disabled'}`);
      loadJobs();
      if (onStatsChange) onStatsChange();
    } catch (error) {
      console.error('Failed to toggle job:', error);
      toast.error('Failed to update job');
    }
  };

  const handleDeleteJob = async (job) => {
    if (!window.confirm(`Are you sure you want to delete the job "${job.JobName}"?`)) {
      return;
    }

    try {
      const tenantCode = getProtectedTenantCode();
      await axios.delete(`/api/automation/jobs/${job.JobID}`, {
        headers: { 'X-Tenant-Code': tenantCode },
      });
      toast.success('Job deleted');
      loadJobs();
      if (onStatsChange) onStatsChange();
    } catch (error) {
      console.error('Failed to delete job:', error);
      toast.error(error.response?.data?.error || 'Failed to delete job');
    }
  };

  const handleEditJob = (job) => {
    setEditingJob(job);
    setShowJobForm(true);
  };

  const handleCreateJob = () => {
    setEditingJob(null);
    setShowJobForm(true);
  };

  const handleCloseForm = () => {
    setShowJobForm(false);
    setEditingJob(null);
    loadJobs();
    if (onStatsChange) onStatsChange();
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'success':
        return <CheckCircle className="h-5 w-5 text-green-500" />;
      case 'failed':
        return <XCircle className="h-5 w-5 text-red-500" />;
      case 'running':
        return <RefreshCw className="h-5 w-5 text-blue-500 animate-spin" />;
      default:
        return <Clock className="h-5 w-5 text-gray-400" />;
    }
  };

  const getExecutionModeColor = (mode) => {
    switch (mode) {
      case 'scheduled':
        return 'bg-blue-100 text-blue-800';
      case 'manual':
        return 'bg-purple-100 text-purple-800';
      case 'api':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="h-8 w-8 text-gray-400 animate-spin" />
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-semibold text-gray-900">Automation Jobs</h2>
        <button
          onClick={handleCreateJob}
          className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-gradient-to-r from-teal-600 to-cyan-600 hover:from-teal-700 hover:to-cyan-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500"
        >
          <Plus className="h-4 w-4 mr-2" />
          Create Job
        </button>
      </div>

      {/* Job List */}
      {jobs.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-lg border-2 border-dashed border-gray-300">
          <Zap className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">No jobs</h3>
          <p className="mt-1 text-sm text-gray-500">
            Get started by creating a new automation job.
          </p>
          <div className="mt-6">
            <button
              onClick={handleCreateJob}
              className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-gradient-to-r from-teal-600 to-cyan-600 hover:from-teal-700 hover:to-cyan-700"
            >
              <Plus className="h-4 w-4 mr-2" />
              Create Job
            </button>
          </div>
        </div>
      ) : (
        <div className="bg-white shadow overflow-hidden sm:rounded-lg">
          <ul className="divide-y divide-gray-200">
            {jobs.map((job) => (
              <li key={job.JobID} className="hover:bg-gray-50">
                <div className="px-6 py-4">
                  <div className="flex items-center justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-3">
                        {/* Status Icon */}
                        {getStatusIcon(job.LastExecutionStatus)}

                        {/* Job Info */}
                        <div className="flex-1">
                          <div className="flex items-center space-x-2">
                            <h3 className="text-lg font-medium text-gray-900 truncate">
                              {job.JobName}
                            </h3>
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getExecutionModeColor(job.ExecutionMode)}`}>
                              {job.ExecutionMode}
                            </span>
                            {!job.IsEnabled && (
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                                Disabled
                              </span>
                            )}
                          </div>
                          <div className="mt-1 flex items-center space-x-4 text-sm text-gray-500">
                            <span className="flex items-center">
                              <Clock className="h-4 w-4 mr-1" />
                              {job.CronSchedule || 'No schedule'}
                            </span>
                            <span>Endpoint: {job.EndpointName}</span>
                            {job.LastExecutionTime && (
                              <span>
                                Last run: {new Date(job.LastExecutionTime).toLocaleString()}
                              </span>
                            )}
                          </div>
                          {job.Description && (
                            <p className="mt-1 text-sm text-gray-600">{job.Description}</p>
                          )}
                          <div className="mt-2 flex items-center space-x-4 text-xs text-gray-500">
                            <span>Executions: {job.ExecutionCount || 0}</span>
                            <span className="text-green-600">Success: {job.SuccessCount || 0}</span>
                            <span className="text-red-600">Failed: {job.FailureCount || 0}</span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center space-x-2 ml-4">
                      <button
                        onClick={() => handleExecuteJob(job)}
                        disabled={!job.IsEnabled || executingJobId === job.JobID}
                        className="p-2 text-green-600 hover:bg-green-50 rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
                        title="Execute now"
                      >
                        {executingJobId === job.JobID ? (
                          <RefreshCw className="h-5 w-5 animate-spin" />
                        ) : (
                          <Play className="h-5 w-5" />
                        )}
                      </button>
                      <button
                        onClick={() => handleToggleJob(job)}
                        className={`p-2 rounded-md ${job.IsEnabled ? 'text-yellow-600 hover:bg-yellow-50' : 'text-gray-400 hover:bg-gray-50'}`}
                        title={job.IsEnabled ? 'Disable' : 'Enable'}
                      >
                        <Power className="h-5 w-5" />
                      </button>
                      <button
                        onClick={() => handleEditJob(job)}
                        className="p-2 text-blue-600 hover:bg-blue-50 rounded-md"
                        title="Edit"
                      >
                        <Edit className="h-5 w-5" />
                      </button>
                      <button
                        onClick={() => handleDeleteJob(job)}
                        className="p-2 text-red-600 hover:bg-red-50 rounded-md"
                        title="Delete"
                      >
                        <Trash2 className="h-5 w-5" />
                      </button>
                    </div>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Job Form Modal */}
      {showJobForm && (
        <JobForm
          job={editingJob}
          onClose={handleCloseForm}
        />
      )}
    </div>
  );
};

export default JobList;

